import React, { useEffect, useState } from 'react'
import { Table, Spin, Empty, Button, message, Space, Modal } from 'antd'
import { DownloadOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons'
import { getStudentCertificates } from '../../../../../Constants/Api/functions/function'
import dayjs from 'dayjs'

const DEFAULT_LAYOUT = {
    studentName: { top: 30, left: 10, fontSize: 26, fontWeight: 'bold', textAlign: 'left', color: '#1a1a1a', visible: true },
    courseName: { top: 42, left: 10, fontSize: 18, fontWeight: 'normal', textAlign: 'left', color: '#444444', visible: true },
    certificateNumber: { top: 54, left: 10, fontSize: 12, fontWeight: 'normal', textAlign: 'left', color: '#666666', visible: true },
    issueDate: { top: 64, left: 10, fontSize: 12, fontWeight: 'normal', textAlign: 'left', color: '#666666', visible: true },
    finalGrade: { top: 74, left: 10, fontSize: 16, fontWeight: 'normal', textAlign: 'left', color: '#1a1a1a', prefix: 'Grade: ', visible: true },
}

const normalizeLayout = (layout) => {
    if (!layout) return DEFAULT_LAYOUT
    return {
        ...DEFAULT_LAYOUT,
        ...layout,
        studentName: { ...DEFAULT_LAYOUT.studentName, ...(layout.studentName || {}) },
        courseName: { ...DEFAULT_LAYOUT.courseName, ...(layout.courseName || {}) },
        certificateNumber: { ...DEFAULT_LAYOUT.certificateNumber, ...(layout.certificateNumber || {}) },
        issueDate: { ...DEFAULT_LAYOUT.issueDate, ...(layout.issueDate || {}) },
        finalGrade: { ...DEFAULT_LAYOUT.finalGrade, ...(layout.finalGrade || {}) },
    }
}

const UserCertificates = ({ user }) => {
    const [certificates, setCertificates] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [viewCertModalOpen, setViewCertModalOpen] = useState(false)
    const [selectedCert, setSelectedCert] = useState(null)
    const [printAspectRatio, setPrintAspectRatio] = useState(1.414)

    useEffect(() => {
        if (user?.id) {
            const fetchUserCertificates = async () => {
                setIsLoading(true)
                try {
                    const data = await getStudentCertificates(user.id)
                    if (Array.isArray(data)) {
                        setCertificates(data)
                    } else {
                        setCertificates([])
                    }
                } catch (error) {
                    console.error('Error fetching certificates:', error)
                    message.error('Failed to fetch certificates')
                    setCertificates([])
                } finally {
                    setIsLoading(false)
                }
            }
            fetchUserCertificates()
        }
    }, [user?.id])

    const handleView = (record) => {
        if (record.TemplateID) {
            setSelectedCert(record)
            setViewCertModalOpen(true)
        } else if (record.CertificateURL) {
            window.open(record.CertificateURL, '_blank')
            message.success('Opening certificate...')
        } else {
            message.warning('Certificate not available for viewing')
        }
    }

    const getFieldTransform = (textAlign) => {
        if (textAlign === 'center') return 'translate(-50%, -50%)'
        if (textAlign === 'right') return 'translate(-100%, -50%)'
        return 'translate(0%, -50%)'
    }

    const columns = [
        {
            title: 'Certificate Number',
            dataIndex: 'CertificateNumber',
            key: 'CertificateNumber',
            render: (text) => text || 'N/A',
        },
        {
            title: 'Course',
            dataIndex: 'courseName',
            key: 'courseName',
            render: (text, record) => text || record.CourseName || 'N/A',
        },
        {
            title: 'Final Grade',
            dataIndex: 'Grade',
            key: 'Grade',
            render: (text) => text || 'Pass',
        },
        {
            title: 'Issue Date',
            dataIndex: 'IssueDate',
            key: 'IssueDate',
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : 'N/A'),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        size='small'
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                    >
                        View
                    </Button>
                    {record.CertificateURL && (
                        <a
                            href={record.CertificateURL}
                            download
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <Button
                                size='small'
                                icon={<DownloadOutlined />}
                            >
                                Download
                            </Button>
                        </a>
                    )}
                </Space>
            ),
        },
    ]

    const currentLayout = selectedCert?.LayoutJSON
        ? normalizeLayout(JSON.parse(selectedCert.LayoutJSON))
        : DEFAULT_LAYOUT

    return (
        <div style={{ padding: '20px' }}>
            <Spin spinning={isLoading} tip='Loading certificates...'>
                {certificates && certificates.length > 0 ? (
                    <Table
                        columns={columns}
                        dataSource={certificates.map((cert, index) => ({
                            ...cert,
                            key: cert.CertificateID || index,
                        }))}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 600 }}
                    />
                ) : (
                    <Empty
                        description='No certificates found'
                        style={{ marginTop: '50px' }}
                    />
                )}
            </Spin>

            <Modal
                title={null}
                open={viewCertModalOpen}
                onCancel={() => setViewCertModalOpen(false)}
                footer={(
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }} className="no-print">
                        <Button
                            type="primary"
                            icon={<PrinterOutlined />}
                            onClick={() => window.print()}
                        >
                            Print Certificate
                        </Button>
                        <Button onClick={() => setViewCertModalOpen(false)}>Close</Button>
                    </div>
                )}
                width={950}
                centered
                className="certificate-print-modal"
                wrapClassName="certificate-print-modal-wrap"
            >
                {viewCertModalOpen && selectedCert && (
                    <style>
                        {`
                          @media print {
                            @page {
                              size: ${printAspectRatio > 1 ? 'landscape' : 'portrait'};
                              margin: 0;
                            }
                          }
                        `}
                    </style>
                )}
                {selectedCert && selectedCert.BackgroundImageURL && (
                    <div className="printable-certificate-outer">
                        <div
                            className="printable-certificate-canvas"
                            style={{
                                position: 'relative',
                                width: '100%',
                                aspectRatio: printAspectRatio,
                                overflow: 'hidden',
                                background: '#ffffff',
                                border: '1px solid #d9d9d9',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            }}
                        >
                            <img
                                src={selectedCert.BackgroundImageURL}
                                alt="Bg Design"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                onLoad={(e) => {
                                    const { naturalWidth, naturalHeight } = e.target;
                                    if (naturalWidth && naturalHeight) {
                                        setPrintAspectRatio(naturalWidth / naturalHeight);
                                    }
                                }}
                            />
                            {currentLayout.studentName?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.studentName.top}%`,
                                        left: `${currentLayout.studentName.left}%`,
                                        transform: getFieldTransform(currentLayout.studentName.textAlign),
                                        fontSize: `${currentLayout.studentName.fontSize}px`,
                                        fontWeight: currentLayout.studentName.fontWeight,
                                        color: currentLayout.studentName.color,
                                        textAlign: currentLayout.studentName.textAlign,
                                        whiteSpace: 'nowrap',
                                        width: 'auto',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {selectedCert.studentName || user?.name}
                                </div>
                            )}
                            {currentLayout.courseName?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.courseName.top}%`,
                                        left: `${currentLayout.courseName.left}%`,
                                        transform: getFieldTransform(currentLayout.courseName.textAlign),
                                        fontSize: `${currentLayout.courseName.fontSize}px`,
                                        fontWeight: currentLayout.courseName.fontWeight,
                                        color: currentLayout.courseName.color,
                                        textAlign: currentLayout.courseName.textAlign,
                                        whiteSpace: 'nowrap',
                                        width: 'auto',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {selectedCert.courseName || selectedCert.CourseName}
                                </div>
                            )}
                            {currentLayout.certificateNumber?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.certificateNumber.top}%`,
                                        left: `${currentLayout.certificateNumber.left}%`,
                                        transform: getFieldTransform(currentLayout.certificateNumber.textAlign),
                                        fontSize: `${currentLayout.certificateNumber.fontSize}px`,
                                        fontWeight: currentLayout.certificateNumber.fontWeight,
                                        color: currentLayout.certificateNumber.color,
                                        textAlign: currentLayout.certificateNumber.textAlign,
                                        whiteSpace: 'nowrap',
                                        width: 'auto',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {selectedCert.CertificateNumber}
                                </div>
                            )}
                            {currentLayout.issueDate?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.issueDate.top}%`,
                                        left: `${currentLayout.issueDate.left}%`,
                                        transform: getFieldTransform(currentLayout.issueDate.textAlign),
                                        fontSize: `${currentLayout.issueDate.fontSize}px`,
                                        fontWeight: currentLayout.issueDate.fontWeight,
                                        color: currentLayout.issueDate.color,
                                        textAlign: currentLayout.issueDate.textAlign,
                                        whiteSpace: 'nowrap',
                                        width: 'auto',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {selectedCert.IssueDate ? dayjs(selectedCert.IssueDate).format('YYYY-MM-DD') : ''}
                                </div>
                            )}
                            {currentLayout.finalGrade?.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${currentLayout.finalGrade.top}%`,
                                        left: `${currentLayout.finalGrade.left}%`,
                                        transform: getFieldTransform(currentLayout.finalGrade.textAlign),
                                        fontSize: `${currentLayout.finalGrade.fontSize}px`,
                                        fontWeight: currentLayout.finalGrade.fontWeight,
                                        color: currentLayout.finalGrade.color,
                                        textAlign: currentLayout.finalGrade.textAlign,
                                        whiteSpace: 'nowrap',
                                        width: 'auto',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {(() => {
                                        const gradeVal = selectedCert.Grade || selectedCert.finalGrade || 'Pass';
                                        const prefix = currentLayout.finalGrade.prefix !== undefined ? currentLayout.finalGrade.prefix : 'Grade: ';
                                        if (String(gradeVal).toLowerCase().startsWith('grade:')) {
                                            return gradeVal;
                                        }
                                        return `${prefix}${gradeVal}`;
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    )
}

export default UserCertificates
